import { logAmplitudeEvent } from 'amplitude/amplitude'
import { requestAutoStringing } from 'reducer/designer/autoString'
import { Dispatch } from 'redux'
import { isActionWhitelisted } from '../actionPermissions'
import { AdaAction } from '../actions'
import { findTargetMessageIndex, getLastMessageIndex } from './helpers'

function validateSystemForAutoString(system: any): void {
  if (!system) {
    throw new Error('No system selected. Please select a system in the design.')
  }

  if (system.inverters().length === 0) {
    throw new Error('Auto String requires at least one inverter to be added to the system.')
  }

  if (system.moduleQuantity() === 0) {
    throw new Error('Auto String requires panels to be placed in the system.')
  }

  if (system.hasMultipleModuleTypes()) {
    throw new Error(
      'Auto Stringing is not available as multiple panels are present on system. Please manually string panel groups.'
    )
  }

  const inverters = system.inverters()
  if (inverters.length > 1 && inverters.some((inverter: any) => inverter.code !== inverters[0].code)) {
    throw new Error('Auto String is only available when a single inverter type is used.')
  }
}

function setAutoStringRunning(dispatch: Dispatch, projectId: number | undefined, isAutoAccepted: boolean) {
  const lastMessageIndex = getLastMessageIndex()
  const toolExecutionState = {
    toolType: 'autoString' as const,
    status: 'running' as const,
    projectId,
    isAutoAccepted,
  }

  if (lastMessageIndex >= 0) {
    dispatch({
      type: 'UPDATE_TOOL_EXECUTION_STATE',
      messageIndex: lastMessageIndex,
      toolExecutionState,
    })
  } else {
    // Fallback: create a new message if there are no messages (shouldn't happen normally)
    dispatch({
      type: 'ADD_MESSAGE',
      content: 'Running auto string...',
      direction: 'receive',
      origin: 'app',
      sharedWithContexts: ['text'],
      toolExecutionState,
    })
  }
}

function updateToolExecutionState(dispatch: Dispatch, projectId: number | undefined, toolExecutionState: any) {
  const targetMessageIndex = findTargetMessageIndex()
  if (targetMessageIndex >= 0) {
    dispatch({
      type: 'UPDATE_TOOL_EXECUTION_STATE',
      messageIndex: targetMessageIndex,
      toolExecutionState: {
        ...toolExecutionState,
        projectId,
      },
    })
  }
}

function handleAutoStringCompletion(dispatch: Dispatch, projectId: number | undefined) {
  updateToolExecutionState(dispatch, projectId, {
    toolType: 'autoString',
    status: 'completed',
    description: 'Auto stringing complete',
  })
  console.log('Auto stringing completed')
}

function handleAutoStringError(dispatch: Dispatch, projectId: number | undefined, errorMessage: string, error?: any) {
  updateToolExecutionState(dispatch, projectId, {
    toolType: 'autoString',
    status: 'error',
    description: errorMessage,
    error: {
      message: errorMessage,
      ...error,
    },
  })
}

function setupAutoStringCompletionMonitor(
  dispatch: Dispatch,
  projectId: number | undefined,
  systemUuid: string
): { unsubscribe: () => void; clearTimeout: () => void } {
  const reduxStore = (window as any).reduxStore
  if (!reduxStore) {
    throw new Error('Redux store not available')
  }

  let isCompleted = false
  let errorTimeoutId: NodeJS.Timeout | null = null

  const unsubscribe = reduxStore.subscribe(() => {
    if (isCompleted) return

    const state = reduxStore.getState()
    const autoStringRunning = state?.designer?.autoString?.running

    // When autoStringRunning becomes undefined, auto-stringing has completed
    if (!autoStringRunning) {
      isCompleted = true
      unsubscribe()

      if (errorTimeoutId) {
        clearTimeout(errorTimeoutId)
        errorTimeoutId = null
      }

      handleAutoStringCompletion(dispatch, projectId)
    }
  })

  errorTimeoutId = setTimeout(() => {
    if (isCompleted) return

    const state = reduxStore.getState()
    const autoStringRunning = state?.designer?.autoString?.running

    if (autoStringRunning) {
      const systemObj = window.editor?.objectByUuid(systemUuid)
      if (!systemObj) {
        isCompleted = true
        unsubscribe()
        handleAutoStringError(dispatch, projectId, 'System no longer available')
      }
    }
  }, 30000)

  return {
    unsubscribe: () => {
      if (!isCompleted) {
        isCompleted = true
        unsubscribe()
        if (errorTimeoutId) {
          clearTimeout(errorTimeoutId)
          errorTimeoutId = null
        }
      }
    },
    clearTimeout: () => {
      if (errorTimeoutId) {
        clearTimeout(errorTimeoutId)
        errorTimeoutId = null
      }
    },
  }
}

export async function handleRunAutoString(action: AdaAction, projectId: number | undefined, dispatch: Dispatch) {
  try {
    const system = window.editor?.selectedSystem
    validateSystemForAutoString(system)

    const inverters = system.inverters()
    console.log('Starting runAutoString for system:', system.uuid)

    logAmplitudeEvent('generic_button_clicked', {
      source: 'Auto String Button Clicked',
      context: 'ada_chat_action',
      system_uuid: system.uuid,
      inverter_id: inverters[0]?.inverter_id,
      inverter_count: inverters.length,
      module_count: system.moduleQuantity(),
    })

    const isAutoAccepted = isActionWhitelisted('runAutoString')
    setAutoStringRunning(dispatch, projectId, isAutoAccepted)

    const firstInverter = inverters[0]
    dispatch(requestAutoStringing(system.uuid, 'best', undefined, firstInverter.inverter_id, true))

    setupAutoStringCompletionMonitor(dispatch, projectId, system.uuid)
  } catch (error: any) {
    console.error('Error running auto string:', error)
    const errorMessage = error?.message || 'Auto stringing failed'
    handleAutoStringError(dispatch, projectId, errorMessage, error)
  }
}
